import { useI18n } from '@i18n';
import { analysis_reference_area_enum, class_of_use_enum, type_of_use_enum } from '@predium/enums';
import isNil from 'lodash/isNil';
import { useQueryStates } from 'nuqs';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import type {
  BenchmarkPortfoliosMap,
  BenchmarkSelection,
} from '@/sections/StatusQuoAnalysis/Components/StatusQuoAnalysisBenchmark';
import { getConsumptionOnlyFiltersReset } from '@/components/building-filters/BuildingFiltersMenuControl';
import { BUILDING_FILTERS_SEARCH_PARAMS } from '@/components/building-filters/search-params';
import {
  DEFAULT_REFERENCE_AREA,
  DEFAULT_REFERENCE_AREA_CONSUMPTION,
} from '@/components/organisms/AnalysisSettings/machines/analysisSettingsMachine';
import { useSession } from '@/components/session/useSession';
import usePosthogTracking from '@/hooks/usePosthogTracking';
import { SEARCH_PARAMS } from '@/routes/search-params';
import { StatusQuoAnalysisTabEnum } from '@/routes/search_params/searchParamsEnums';
import {
  BENCHMARK_ENTIRE_COMPANY_ID,
  getSameClassOfUseBenchmarkLabel,
  getSameTypeOfUseBenchmarkLabel,
} from '@/sections/StatusQuoAnalysis/Components/benchmarkCompareOptions';
import { useRestoreLastActiveTab } from '@/sections/StatusQuoAnalysis/hooks/useRestoreLastActiveTab';
import { storeActiveTab } from './activeTabStorage';
import { restoreTabFilters, stashTabFilters } from './tabScopedBuildingFilters';

type StatusQuoAnalysisTabAndBenchmarkContextType = {
  activeTab: NonNullable<StatusQuoAnalysisTabEnum | null>;
  // null = no comparison; 0 = entire company, positive number = a specific portfolio.
  activeBenchmark: BenchmarkSelection;
  activeBenchmarkClassOfUse: class_of_use_enum | null;
  activeBenchmarkTypeOfUse: type_of_use_enum | null;
  isBenchmarkActive: boolean;
  benchmarkPortfolioId: number | null;
  handleTabChange: (tab: StatusQuoAnalysisTabEnum) => void;
  handleBenchmarkChange: (benchmark: BenchmarkSelection, shouldTrackBenchmarkChange?: boolean) => void;
  handleBenchmarkClassOfUseChange: (classOfUse: class_of_use_enum) => void;
  handleBenchmarkTypeOfUseChange: (typeOfUse: type_of_use_enum) => void;
  setBenchmarkPortfoliosMap: (benchmarkPortfoliosMap: BenchmarkPortfoliosMap) => void;
  activeBenchmarkName: string;
};

const InitialStatusQuoAnalysisTabAndBenchmarkContext: StatusQuoAnalysisTabAndBenchmarkContextType = {
  activeTab: StatusQuoAnalysisTabEnum.DEMAND,
  activeBenchmark: null,
  activeBenchmarkClassOfUse: null,
  activeBenchmarkTypeOfUse: null,
  isBenchmarkActive: false,
  benchmarkPortfolioId: null,
  handleTabChange: () => {},
  handleBenchmarkChange: () => {},
  handleBenchmarkClassOfUseChange: () => {},
  handleBenchmarkTypeOfUseChange: () => {},
  setBenchmarkPortfoliosMap: () => {},
  activeBenchmarkName: '',
};

const StatusQuoAnalysisTabAndBenchmarkContext = createContext(InitialStatusQuoAnalysisTabAndBenchmarkContext);

type Props = PropsWithChildren<{
  storageKey?: string;
}>;

export const StatusQuoAnalysisTabAndBenchmarkContextProvider = ({ children, storageKey }: Props) => {
  const { t } = useI18n();
  const { trackEvent } = usePosthogTracking();
  const { featureFlags } = useSession();
  const [queryParams, setQueryParams] = useQueryStates(SEARCH_PARAMS.statusQuoAnalysis.building);
  const [filterParams, setFilterParams] = useQueryStates(BUILDING_FILTERS_SEARCH_PARAMS);

  const activeTab =
    queryParams.tab === StatusQuoAnalysisTabEnum.CONSUMPTION && !featureFlags.CONSUMPTION_STATUS_QUO_TAB_ENABLED
      ? StatusQuoAnalysisTabEnum.DEMAND
      : queryParams.tab;
  const rawBenchmark = queryParams.benchmark;
  // When "no comparison" is disabled, the legacy default applies: an absent
  // benchmark means the entire company, so there is always an active comparison.
  const legacyBenchmark = isNil(rawBenchmark) ? BENCHMARK_ENTIRE_COMPANY_ID : rawBenchmark;
  const activeBenchmark = featureFlags.NO_COMPARISON_BENCHMARK ? rawBenchmark : legacyBenchmark;
  const activeBenchmarkClassOfUse = queryParams.classOfUse;
  const activeBenchmarkTypeOfUse = queryParams.typeOfUse;

  const isBenchmarkActive =
    !isNil(activeBenchmark) || !isNil(activeBenchmarkClassOfUse) || !isNil(activeBenchmarkTypeOfUse);
  const benchmarkPortfolioId = activeBenchmark === BENCHMARK_ENTIRE_COMPANY_ID ? null : activeBenchmark;

  useRestoreLastActiveTab(storageKey);

  const handleTabChange = useCallback(
    (tab: StatusQuoAnalysisTabEnum) => {
      trackEvent('BUILDING_STATUS_QUO_ANALYSIS_TAB_CHANGED', {
        display_base: tab,
      });

      // If the current referenceArea is the old tab's default, switch to the new tab's default.
      const oldDefault =
        activeTab === StatusQuoAnalysisTabEnum.CONSUMPTION
          ? DEFAULT_REFERENCE_AREA_CONSUMPTION
          : DEFAULT_REFERENCE_AREA;
      const newDefault =
        tab === StatusQuoAnalysisTabEnum.CONSUMPTION ? DEFAULT_REFERENCE_AREA_CONSUMPTION : DEFAULT_REFERENCE_AREA;
      const currentReferenceArea: analysis_reference_area_enum = queryParams.referenceAreaType ?? oldDefault;
      const newReferenceArea = currentReferenceArea === oldDefault ? newDefault : currentReferenceArea;

      // Both branches reset the benchmark to "no comparison" (null).
      setQueryParams({
        tab,
        benchmark: null,
        classOfUse: null,
        typeOfUse: null,
        referenceAreaType: newReferenceArea,
      });

      if (storageKey) {
        storeActiveTab(storageKey, tab);

        // Stash the outgoing tab's filters, restore the incoming tab's.
        stashTabFilters(storageKey, activeTab, filterParams);
        void setFilterParams(restoreTabFilters(storageKey, tab));
      } else if (tab !== StatusQuoAnalysisTabEnum.CONSUMPTION) {
        // hasConsumptionData only applies to the consumption tab — clear it when switching away.
        void setFilterParams(getConsumptionOnlyFiltersReset());
      }
    },
    [activeTab, filterParams, queryParams.referenceAreaType, setFilterParams, setQueryParams, storageKey, trackEvent],
  );

  const [benchmarkPortfoliosMap, setBenchmarkPortfoliosMap] = useState<BenchmarkPortfoliosMap>(new Map());
  const handleBenchmarkChange = useCallback(
    (benchmark: BenchmarkSelection, shouldTrackBenchmarkChange = true) => {
      if (shouldTrackBenchmarkChange) {
        trackEvent('BUILDING_STATUS_QUO_ANALYSIS_BENCHMARK_CHANGED', {
          display_base: benchmark,
        });
      }
      // Picking a portfolio (or Total Asset Base) clears the use-type scopes.
      setQueryParams({ benchmark, classOfUse: null, typeOfUse: null });
    },
    [setQueryParams, trackEvent],
  );

  const handleBenchmarkClassOfUseChange = useCallback(
    (classOfUse: class_of_use_enum) => {
      trackEvent('BUILDING_STATUS_QUO_ANALYSIS_BENCHMARK_CHANGED', {
        display_base: null,
        class_of_use: classOfUse,
      });
      setQueryParams({ benchmark: null, classOfUse, typeOfUse: null });
    },
    [setQueryParams, trackEvent],
  );

  const handleBenchmarkTypeOfUseChange = useCallback(
    (typeOfUse: type_of_use_enum) => {
      trackEvent('BUILDING_STATUS_QUO_ANALYSIS_BENCHMARK_CHANGED', {
        display_base: null,
        type_of_use: typeOfUse,
      });
      setQueryParams({ benchmark: null, classOfUse: null, typeOfUse });
    },
    [setQueryParams, trackEvent],
  );

  const activeBenchmarkName = useMemo(() => {
    if (!isBenchmarkActive) {
      return '';
    }
    if (!isNil(activeBenchmarkTypeOfUse)) {
      return getSameTypeOfUseBenchmarkLabel(t, activeBenchmarkTypeOfUse);
    }
    if (!isNil(activeBenchmarkClassOfUse)) {
      return getSameClassOfUseBenchmarkLabel(t, activeBenchmarkClassOfUse);
    }
    return benchmarkPortfoliosMap.get(activeBenchmark) ?? '';
  }, [
    isBenchmarkActive,
    t,
    activeBenchmarkTypeOfUse,
    activeBenchmarkClassOfUse,
    benchmarkPortfoliosMap,
    activeBenchmark,
  ]);

  const contextValue = useMemo(
    () => ({
      activeTab,
      activeBenchmark,
      activeBenchmarkClassOfUse,
      activeBenchmarkTypeOfUse,
      isBenchmarkActive,
      benchmarkPortfolioId,
      handleTabChange,
      handleBenchmarkChange,
      handleBenchmarkClassOfUseChange,
      handleBenchmarkTypeOfUseChange,
      setBenchmarkPortfoliosMap,
      activeBenchmarkName,
    }),
    [
      activeTab,
      activeBenchmark,
      activeBenchmarkClassOfUse,
      activeBenchmarkTypeOfUse,
      isBenchmarkActive,
      benchmarkPortfolioId,
      handleTabChange,
      handleBenchmarkChange,
      handleBenchmarkClassOfUseChange,
      handleBenchmarkTypeOfUseChange,
      setBenchmarkPortfoliosMap,
      activeBenchmarkName,
    ],
  );

  return (
    <StatusQuoAnalysisTabAndBenchmarkContext.Provider value={contextValue}>
      {children}
    </StatusQuoAnalysisTabAndBenchmarkContext.Provider>
  );
};

export function useStatusQuoAnalysisTabAndBenchmarkContext() {
  const context = useContext(StatusQuoAnalysisTabAndBenchmarkContext);
  if (!context)
    throw new Error(
      'useStatusQuoAnalysisTabAndBenchmarkContext must be used within a StatusQuoAnalysisTabAndBenchmarkContextProvider',
    );
  return context;
}
